import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { HiCalendar, HiSearch } from 'react-icons/hi';
import classNames from 'classnames';
import EmptyState from '../../../../components/misc/EmptyState';
import EventsTable from '../../../../components/misc/EventsTable';
import { PersonalEvent as Event, EventParticipationAnswer } from '../../../../types/event';
import { getUserId } from '../../../../services/httpService';
import { respondToEvent } from '../../../../services/eventService';

interface AgendaListViewProps {
  events: Event[];
  loading: boolean;
}

type EventFilter = 'upcoming' | 'past';

export default function AgendaListView({ events, loading }: AgendaListViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [eventFilter, setEventFilter] = useState<EventFilter>('upcoming');
  const [localEvents, setLocalEvents] = useState<Event[]>(events);

  // Sync local events with props
  useEffect(() => {
    setLocalEvents(events);
  }, [events]);

  const filteredEvents = useMemo(() => {
    if (!searchTerm.trim()) return localEvents;

    const lowerSearch = searchTerm.toLowerCase();
    return localEvents.filter(
      (event) =>
        event.title.toLowerCase().includes(lowerSearch) ||
        event.description?.toLowerCase().includes(lowerSearch) ||
        event.location?.toLowerCase().includes(lowerSearch),
    );
  }, [localEvents, searchTerm]);

  const groupedEvents = useMemo(() => {
    const now = new Date();
    const upcoming: Event[] = [];
    const past: Event[] = [];

    filteredEvents.forEach((event) => {
      const eventDate = new Date(event.startTime);
      if (eventDate >= now) {
        upcoming.push(event);
      } else {
        past.push(event);
      }
    });

    // Sort upcoming events by start time (ascending)
    upcoming.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

    // Sort past events by start time (descending)
    past.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

    return { upcoming, past };
  }, [filteredEvents]);

  const getCurrentUserParticipant = (event: Event) => {
    const userId = getUserId();
    if (!userId) return undefined;
    return event.participants?.find((p) => p.id === userId);
  };

  const handleRsvpChange = async (eventId: number, answer: EventParticipationAnswer) => {
    const userId = getUserId();
    if (!userId) return;

    setLocalEvents((prevEvents) =>
      prevEvents.map((event) => {
        if (event.id !== eventId) return event;

        const updatedParticipants = event.participants?.map((p) => (p.id === userId ? { ...p, answer } : p)) || [];

        return { ...event, participants: updatedParticipants };
      }),
    );

    try {
      await respondToEvent(eventId.toString(), answer);
    } catch (error) {
      console.error('Failed to update RSVP:', error);
      setLocalEvents(events);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-details">{t('general.loading')}</p>
      </div>
    );
  }

  if (localEvents.length === 0) {
    return (
      <EmptyState icon={<HiCalendar className="h-16 w-16 text-primary" />} title={t('page.dashboard.event.noEvents')} />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Search Bar and Filter Buttons */}
      <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <HiSearch className="h-5 w-5 text-primary" />
          </div>
          <input
            type="search"
            placeholder={t('page.dashboard.event.searchEvents')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg !bg-secondary-50 py-2 pl-10 pr-4 text-primary focus:outline-none focus:ring-0 sm:w-1/3"
          />
        </div>
        <div className="flex w-full flex-nowrap items-center justify-start gap-1 rounded-lg bg-secondary-50 p-1 sm:w-auto sm:justify-end">
          <button
            type="button"
            onClick={() => setEventFilter('upcoming')}
            className={classNames(
              'flex-1 rounded-md px-3 py-1 text-center text-sm font-medium text-primary transition-all sm:flex-none sm:px-4 sm:py-2 sm:text-base',
              {
                '!bg-secondary': eventFilter === 'upcoming',
              },
            )}>
            {t('page.dashboard.event.upcomingEvents')}
          </button>
          <button
            type="button"
            onClick={() => setEventFilter('past')}
            className={classNames(
              'flex-1 rounded-md px-3 py-1 text-center text-sm font-medium text-primary transition-all sm:flex-none sm:px-4 sm:py-2 sm:text-base',
              {
                '!bg-secondary': eventFilter === 'past',
              },
            )}>
            {t('page.dashboard.event.pastEvents')}
          </button>
        </div>
      </div>

      {filteredEvents.length === 0 ? (
        <EmptyState
          icon={<HiCalendar className="h-16 w-16 text-primary" />}
          title={t('page.dashboard.event.noEventsFound')}
        />
      ) : (
        <>
          {/* Upcoming Events */}
          {eventFilter === 'upcoming' && groupedEvents.upcoming.length === 0 && (
            <EmptyState
              icon={<HiCalendar className="h-16 w-16 text-primary" />}
              title={t('page.dashboard.event.noEventsFound')}
            />
          )}
          {eventFilter === 'upcoming' && groupedEvents.upcoming.length > 0 && (
            <EventsTable
              events={groupedEvents.upcoming}
              onRsvpChange={(id, answer) => handleRsvpChange(id, answer)}
              getCurrentUserParticipant={(event) => getCurrentUserParticipant(event)}
              onBuyTicket={(id) => navigate(`/event/invite`, { state: { eventId: id } })}
            />
          )}

          {/* Past Events */}
          {eventFilter === 'past' && groupedEvents.past.length === 0 && (
            <EmptyState
              icon={<HiCalendar className="h-16 w-16 text-primary" />}
              title={t('page.dashboard.event.noEventsFound')}
            />
          )}
          {eventFilter === 'past' && groupedEvents.past.length > 0 && (
            <EventsTable
              events={groupedEvents.past}
              onRsvpChange={(id, answer) => handleRsvpChange(id, answer)}
              getCurrentUserParticipant={(event) => getCurrentUserParticipant(event)}
              onBuyTicket={(id) => navigate(`/event/invite`, { state: { eventId: id } })}
            />
          )}
        </>
      )}
    </div>
  );
}
